import React from "react";
import { useNavigate } from "react-router-dom";
import HomePageMap from "../components/maps/HomePageMap";
import Faq from "../components/common/Faq";
import { useAuth } from "../hooks/useAuth";

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-primary-900 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl md:text-6xl">
              <span className="block">D-ESPIAL</span>
              <span className="block text-primary-400">Threat Management System</span>
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-300 sm:text-2xl">
              Advanced security intelligence platform providing real-time threat
              analysis, incident reporting, and comprehensive security
              assessments across global regions.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center text-gray-300">
                <svg
                  className="h-5 w-5 text-primary-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Real-time Intelligence
              </div>
              <div className="flex items-center text-gray-300">
                <svg
                  className="h-5 w-5 text-primary-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Comprehensive Coverage
              </div>
              <div className="flex items-center text-gray-300">
                <svg
                  className="h-5 w-5 text-primary-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Professional Reports
              </div>
            </div>
            <div className="mt-10 max-w-md mx-auto sm:flex sm:justify-center md:mt-12">
              {!isAuthenticated && (
                <div className="space-y-3 sm:space-y-0 sm:space-x-4 sm:flex">
                  <button
                    onClick={() => navigate("/signup")}
                    className="w-full flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-lg text-gray-900 bg-primary-400 hover:bg-primary-300 transition-colors duration-200 md:py-4 md:text-lg md:px-12"
                  >
                    Start Free Trial
                  </button>
                  <button
                    onClick={() => navigate("/signin")}
                    className="w-full flex items-center justify-center px-8 py-4 border-2 border-primary-400 text-base font-medium rounded-lg text-primary-400 bg-transparent hover:bg-primary-400 hover:text-gray-900 transition-colors duration-200 md:py-4 md:text-lg md:px-12"
                  >
                    Sign In
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600">15+</div>
              <div className="mt-2 text-sm font-medium text-gray-500 uppercase tracking-wide">
                Countries Covered
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600">10K+</div>
              <div className="mt-2 text-sm font-medium text-gray-500 uppercase tracking-wide">
                Security Reports
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600">24/7</div>
              <div className="mt-2 text-sm font-medium text-gray-500 uppercase tracking-wide">
                Monitoring
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600">500+</div>
              <div className="mt-2 text-sm font-medium text-gray-500 uppercase tracking-wide">
                Active Users
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Map Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Interactive Security Intelligence Map
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Explore global security threats in real-time. Click any region to view detailed 
              intelligence reports and incident analysis. From country-level overviews to 
              local government area details.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <HomePageMap 
              className="w-full h-96 lg:h-[500px]" 
            />
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-extrabold text-gray-900">
                Advanced Security Intelligence Platform
              </h2>
              <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive threat analysis and incident reporting system
                designed for security professionals, government agencies, and
                organizations operating worldwide.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start">
                  <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white">
                    <svg
                      className="h-8 w-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="mt-6 text-2xl font-bold text-gray-900">
                  Geospatial Intelligence
                </h3>
                <p className="mt-4 text-lg text-gray-600">
                  Interactive mapping system with hierarchical navigation from
                  regional overviews to local government area details. Powered
                  by OpenStreetMap data with real-time incident overlays.
                </p>
                <ul className="mt-6 space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Country → State → LGA Navigation
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Real-time Incident Mapping
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Custom Boundary Analysis
                  </li>
                </ul>
              </div>

              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start">
                  <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white">
                    <svg
                      className="h-8 w-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="mt-6 text-2xl font-bold text-gray-900">
                  Automated Intelligence
                </h3>
                <p className="mt-4 text-lg text-gray-600">
                  AI-powered data collection and analysis system that
                  continuously monitors security developments across multiple
                  sources and provides actionable intelligence.
                </p>
                <ul className="mt-6 space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Automated Web Scraping
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Real-time Alerts & Notifications
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Multi-source Data Fusion
                  </li>
                </ul>
              </div>

              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start">
                  <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white">
                    <svg
                      className="h-8 w-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="mt-6 text-2xl font-bold text-gray-900">
                  Professional Reports
                </h3>
                <p className="mt-4 text-lg text-gray-600">
                  Comprehensive security assessments and detailed incident
                  reports in professional formats. Export capabilities for
                  presentations, briefings, and strategic planning.
                </p>
                <ul className="mt-6 space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    PDF & PowerPoint Export
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Custom Security Plans
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Executive Briefings
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Tiers Section */}
        <div className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-extrabold text-gray-900">
                Choose Your Intelligence Level
              </h2>
              <p className="mt-6 text-xl text-gray-600">
                Flexible subscription plans designed for different
                organizational needs and security requirements.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Gold Tier */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200 hover:border-primary-300 transition-colors">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900">Gold</h3>
                  <div className="mt-4 flex items-center justify-center">
                    <span className="text-4xl font-extrabold text-gray-900">
                      $50
                    </span>
                    <span className="text-lg font-medium text-gray-500 ml-2">
                      /month
                    </span>
                  </div>
                  <p className="mt-4 text-gray-600">
                    Perfect for individual security professionals
                  </p>
                </div>
                <ul className="mt-8 space-y-4">
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    View LGA level reports
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Unlimited LGA access
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Email alerts
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    View-only access
                  </li>
                </ul>
              </div>

              {/* Platinum Tier */}
              <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-primary-500 relative">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900">Platinum</h3>
                  <div className="mt-4 flex items-center justify-center">
                    <span className="text-4xl font-extrabold text-gray-900">
                      $75
                    </span>
                    <span className="text-lg font-medium text-gray-500 ml-2">
                      /month
                    </span>
                  </div>
                  <p className="mt-4 text-gray-600">
                    Ideal for teams and organizations
                  </p>
                </div>
                <ul className="mt-8 space-y-4">
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Everything in Gold
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    View State & Country reports
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Up to 5 team members
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    API access (limited)
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Priority support
                  </li>
                </ul>
              </div>

              {/* Diamond Tier */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200 hover:border-primary-300 transition-colors">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900">Diamond</h3>
                  <div className="mt-4 flex items-center justify-center">
                    <span className="text-4xl font-extrabold text-gray-900">
                      $150
                    </span>
                    <span className="text-lg font-medium text-gray-500 ml-2">
                      /month
                    </span>
                  </div>
                  <p className="mt-4 text-gray-600">
                    Enterprise solution with full download access
                  </p>
                </div>
                <ul className="mt-8 space-y-4">
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Everything in Platinum
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Download all reports
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Full API access
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Unlimited users
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-5 w-5 text-primary-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    White-label rights
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <Faq />

        {/* CTA Section */}
        {!isAuthenticated && (
          <div className="bg-gradient-to-r from-primary-700 to-primary-800">
            <div className="max-w-4xl mx-auto text-center py-20 px-4 sm:py-24 sm:px-6 lg:px-8">
              <h2 className="text-4xl font-extrabold text-white sm:text-5xl">
                <span className="block">Ready to Secure your Mobile and Static Assets Worldwide?</span>
                <span className="block text-primary-300 mt-2">
                  Start Your Intelligence Journey Today
                </span>
              </h2>
              <p className="mt-6 text-xl leading-8 text-primary-100 max-w-2xl mx-auto">
                Join security professionals, government agencies, and
                organizations who rely on DTMS for critical threat intelligence
                and comprehensive security assessments.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <button
                  onClick={() => navigate("/signup")}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-primary-700 bg-white hover:bg-gray-50 transition-colors duration-200 shadow-lg"
                >
                  Start Free Trial
                  <svg
                    className="ml-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => navigate("/signin")}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border-2 border-primary-300 text-lg font-medium rounded-lg text-primary-100 bg-transparent hover:bg-primary-600 transition-colors duration-200"
                >
                  Sign In
                </button>
              </div>
              <p className="mt-6 text-sm text-primary-200">
                No credit card required • 14-day free trial • Cancel anytime
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
